import { useState, useMemo, useEffect } from "react";
import { FileText, Download, Loader2, AlertCircle, ChevronDown, ChevronRight, Search, FileSignature } from "lucide-react";
import GuideBanner from "../components/GuideBanner";
import Badge from "../components/Badge";
import Pagination from "../components/Pagination";
import { SISTEMAS } from "../constants";
import { supabase } from "../lib/supabase";
import { MODULOS_TERMO, carregarVersoes, carregarAceitesPorFicha, nomeArquivo } from "../lib/contratos";
import { gerarTermoUsoPDF, baixarBlob } from "../utils/gerarTermoPDF";
import { credStatus, fmtDate } from "../utils";

// Onde se busca o contrato que um fornecedor pede. Dois tipos de arquivo:
//   • o modelo do Termo de Uso de cada módulo — para quem quer ler antes de aderir;
//   • a via do fornecedor — a ficha que ele assinou e o termo que ele aceitou,
//     no texto exato da versão que leu, com o comprovante do aceite no fim.
// Rastreamento não tem termo próprio: para ele o contrato é a ficha.

const fmtVersaoData = (iso) => (iso ? fmtDate(String(iso).slice(0, 10)) : "—");

export default function ContratosParceiros({ credenciamentos = [], search = "" }) {
  const [versoes, setVersoes] = useState(null); // { MODULO: [versões, mais nova primeiro] }
  const [aceites, setAceites] = useState(null); // { fichaId: [aceite por módulo] }
  const [erroAceites, setErroAceites] = useState("");
  const [baixando, setBaixando] = useState(""); // chave do botão em andamento
  const [erro, setErro] = useState("");
  const [abertoHist, setAbertoHist] = useState({});
  const [q, setQ] = useState("");
  const [sistemaFilter, setSistemaFilter] = useState("all");
  const [soConcluidas, setSoConcluidas] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    let vivo = true;
    Promise.all(MODULOS_TERMO.map((m) => carregarVersoes(m.modulo))).then((listas) => {
      if (!vivo) return;
      setVersoes(Object.fromEntries(MODULOS_TERMO.map((m, i) => [m.modulo, listas[i]])));
    });
    carregarAceitesPorFicha().then((r) => {
      if (!vivo) return;
      if (r.ok) setAceites(r.porFicha);
      else { setAceites({}); setErroAceites(r.erro); }
    });
    return () => { vivo = false; };
  }, []);

  // Só entra quem tem algo para baixar: a ficha gerada ou ao menos um aceite.
  const fornecedores = useMemo(() => {
    setPage(1);
    const texto = (q || search || "").toLowerCase().trim();
    return credenciamentos
      .filter((c) => c.fichaPath || (aceites?.[c.id] || []).length)
      .filter((c) => !soConcluidas || credStatus(c).id === "concluido")
      .filter((c) => sistemaFilter === "all" || (c.sistemas || []).includes(sistemaFilter))
      .filter((c) => !texto || `${c.razaoSocial} ${c.nomeFantasia} ${c.cnpj} ${c.cidade}`.toLowerCase().includes(texto))
      .sort((a, b) => (a.nomeFantasia || a.razaoSocial || "").localeCompare(b.nomeFantasia || b.razaoSocial || "", "pt-BR"));
  }, [credenciamentos, aceites, q, search, sistemaFilter, soConcluidas]);

  const paginated = useMemo(() => fornecedores.slice((page - 1) * pageSize, page * pageSize), [fornecedores, page, pageSize]);

  const rodar = async (chave, fn) => {
    setErro(""); setBaixando(chave);
    try { await fn(); } catch (e) { setErro(e?.message || "Não foi possível gerar o arquivo."); }
    finally { setBaixando(""); }
  };

  const baixarModelo = (m, v) => rodar(`modelo:${m.modulo}:${v.codigo}:${v.versao}`, async () => {
    const blob = await gerarTermoUsoPDF({ termo: v, moduloLabel: m.label });
    baixarBlob(blob, `${nomeArquivo("termo-de-uso", m.label, v.codigo, `v${v.versao}`)}.pdf`);
  });

  const baixarFicha = (c) => rodar(`ficha:${c.id}`, async () => {
    const { data, error } = await supabase.storage.from("fichas").download(c.fichaPath);
    if (error || !data) throw new Error("O PDF da ficha não foi encontrado no armazenamento.");
    baixarBlob(data, `${nomeArquivo("ficha-credenciamento", c.nomeFantasia || c.razaoSocial || c.id)}.pdf`);
  });

  const baixarTermoAceito = (c, a) => rodar(`aceite:${a.id}`, async () => {
    const m = MODULOS_TERMO.find((x) => x.modulo === a.modulo);
    const v = (versoes?.[a.modulo] || []).find((x) => x.codigo === a.codigo && String(x.versao) === String(a.versao));
    if (!v) throw new Error(`O texto da versão ${a.codigo} ${a.versao} não foi encontrado.`);
    const blob = await gerarTermoUsoPDF({ termo: v, moduloLabel: m?.label || a.modulo, aceite: a, fornecedor: c });
    baixarBlob(blob, `${nomeArquivo("termo-aceito", m?.label || a.modulo, c.nomeFantasia || c.razaoSocial)}.pdf`);
  });

  const sel = { padding: "7px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 12, background: "#fff", cursor: "pointer", fontFamily: "inherit" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <GuideBanner
        title="Contratos de Parceiros — o que mandar a quem pede"
        text="Em cima, o modelo do Termo de Uso de cada módulo, para quem quer ler antes de aderir. Embaixo, a via de cada fornecedor: a ficha que ele assinou e o termo que ele aceitou, no texto exato da versão que leu, com o comprovante do aceite. Rastreamento não tem termo próprio: o contrato dele é a ficha."
      />

      {erro && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 12px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", fontSize: 12, color: "#b91c1c" }}>
          <AlertCircle size={14} /> {erro}
        </div>
      )}

      {/* ── Modelos dos termos ── */}
      <Secao titulo="Modelos dos termos">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
          {MODULOS_TERMO.map((m) => {
            const lista = versoes?.[m.modulo];
            const vigente = lista?.find((v) => !v.vigencia_fim);
            const anteriores = (lista || []).filter((v) => v !== vigente);
            return (
              <div key={m.modulo} style={{ background: "#fff", borderRadius: 10, padding: "14px 16px", border: "1px solid #e5e7eb", borderLeft: `4px solid ${m.color}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                  <FileText size={14} color={m.color} /> Termo de Uso — {m.label}
                </div>
                {lista === undefined || versoes === null ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#64748b", marginTop: 8 }}><Loader2 size={12} className="spin" /> Carregando...</div>
                ) : !vigente ? (
                  <div style={{ fontSize: 11, color: "#b45309", marginTop: 8 }}>Nenhuma versão vigente publicada.</div>
                ) : (
                  <>
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{vigente.codigo} · versão {vigente.versao} · vigente desde {fmtVersaoData(vigente.vigencia_inicio)}</div>
                    <BotaoBaixar onClick={() => baixarModelo(m, vigente)} carregando={baixando === `modelo:${m.modulo}:${vigente.codigo}:${vigente.versao}`} style={{ marginTop: 10 }}>
                      Baixar versão vigente
                    </BotaoBaixar>
                  </>
                )}
                {anteriores.length > 0 && (
                  <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid #f1f5f9" }}>
                    <button onClick={() => setAbertoHist((p) => ({ ...p, [m.modulo]: !p[m.modulo] }))} style={{ display: "flex", alignItems: "center", gap: 4, border: "none", background: "none", padding: 0, fontSize: 11, fontWeight: 600, color: "#475569", cursor: "pointer" }}>
                      {abertoHist[m.modulo] ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Versões anteriores ({anteriores.length})
                    </button>
                    {abertoHist[m.modulo] && anteriores.map((v) => (
                      <div key={`${v.codigo}-${v.versao}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 6, fontSize: 11, color: "#64748b" }}>
                        <span>{v.codigo} · v{v.versao} · {fmtVersaoData(v.vigencia_inicio)} a {fmtVersaoData(v.vigencia_fim)}</span>
                        <BotaoBaixar pequeno onClick={() => baixarModelo(m, v)} carregando={baixando === `modelo:${m.modulo}:${v.codigo}:${v.versao}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ background: "#f8fafc", borderRadius: 10, padding: "14px 16px", border: "1px dashed #cbd5e1", borderLeft: "4px solid #2563eb" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
              <FileText size={14} color="#2563eb" /> Rastreamento
            </div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 4, lineHeight: 1.45 }}>
              Sem termo próprio por enquanto. O contrato do fornecedor de rastreamento é a ficha de credenciamento assinada, que se baixa na lista abaixo.
            </div>
          </div>
        </div>
      </Secao>

      {/* ── Via de cada fornecedor ── */}
      <Secao titulo="Contratos por fornecedor">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <div style={{ position: "relative", flex: "1 1 220px", maxWidth: 340 }}>
            <Search size={13} color="#94a3b8" style={{ position: "absolute", left: 9, top: 9 }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Empresa, CNPJ, cidade..." style={{ ...sel, cursor: "text", width: "100%", paddingLeft: 28, boxSizing: "border-box" }} />
          </div>
          <select value={sistemaFilter} onChange={(e) => setSistemaFilter(e.target.value)} style={sel}>
            <option value="all">Todos os sistemas</option>
            {SISTEMAS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#475569", cursor: "pointer" }}>
            <input type="checkbox" checked={soConcluidas} onChange={(e) => setSoConcluidas(e.target.checked)} /> Só fichas concluídas
          </label>
          <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: "auto" }}>{fornecedores.length} fornecedor(es)</span>
        </div>

        {erroAceites && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", marginBottom: 10, borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 12, color: "#92400e" }}>
            <AlertCircle size={13} /> {erroAceites} As fichas continuam disponíveis; os termos aceitos não.
          </div>
        )}

        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f8fafc", textAlign: "left", color: "#64748b", fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                <th style={th}>Fornecedor</th>
                <th style={th}>Sistemas</th>
                <th style={th}>Ficha assinada</th>
                <th style={th}>Termos aceitos</th>
              </tr>
            </thead>
            <tbody>
              {aceites === null ? (
                <tr><td colSpan={4} style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}><Loader2 size={14} className="spin" style={{ verticalAlign: "-2px" }} /> Carregando contratos...</td></tr>
              ) : paginated.length === 0 ? (
                <tr><td colSpan={4} style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>
                  <FileSignature size={24} style={{ opacity: 0.4, display: "block", margin: "0 auto 6px" }} />
                  Nenhum contrato encontrado{soConcluidas ? " entre as fichas concluídas" : ""}.
                </td></tr>
              ) : paginated.map((c) => {
                const lista = aceites[c.id] || [];
                return (
                  <tr key={c.id} style={{ borderTop: "1px solid #f1f5f9", verticalAlign: "top" }}>
                    <td style={td}>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>{c.nomeFantasia || c.razaoSocial || "Sem nome"}</div>
                      <div style={{ fontSize: 10.5, color: "#64748b" }}>{[c.cnpj, [c.cidade, c.estado].filter(Boolean).join("/")].filter(Boolean).join(" · ")}</div>
                    </td>
                    <td style={td}>
                      <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                        {(c.sistemas || []).map((id) => { const s = SISTEMAS.find((x) => x.id === id); return s ? <Badge key={id} color={s.color} small>{s.label}</Badge> : null; })}
                      </div>
                    </td>
                    <td style={td}>
                      {c.fichaPath
                        ? <BotaoBaixar pequeno onClick={() => baixarFicha(c)} carregando={baixando === `ficha:${c.id}`}>Ficha (PDF)</BotaoBaixar>
                        : <span style={{ fontSize: 11, color: "#cbd5e1" }}>Não gerada</span>}
                    </td>
                    <td style={td}>
                      {lista.length === 0 ? (
                        <span style={{ fontSize: 11, color: "#cbd5e1" }}>Nenhum aceite registrado</span>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                          {lista.map((a) => {
                            const m = MODULOS_TERMO.find((x) => x.modulo === a.modulo);
                            return (
                              <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                <BotaoBaixar pequeno onClick={() => baixarTermoAceito(c, a)} carregando={baixando === `aceite:${a.id}`} disabled={!versoes}>
                                  {m?.label || a.modulo}
                                </BotaoBaixar>
                                <span style={{ fontSize: 10.5, color: "#64748b" }}>{a.codigo} v{a.versao} · {fmtVersaoData(a.aceito_em)}</span>
                                {/* O texto daquela versão foi mexido depois do aceite:
                                    o PDF não reproduz mais o que a pessoa leu. */}
                                {!a.integro && (
                                  <span title="O texto desta versão mudou depois do aceite — o hash não confere. Confira antes de enviar." style={{ fontSize: 9, fontWeight: 700, color: "#b91c1c", background: "#fef2f2", padding: "1px 6px", borderRadius: 10 }}>HASH NÃO CONFERE</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination page={page} totalItems={fornecedores.length} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
        </div>
      </Secao>
    </div>
  );
}

function Secao({ titulo, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>{titulo}</div>
      {children}
    </div>
  );
}

function BotaoBaixar({ onClick, carregando, disabled, pequeno, children, style }) {
  const off = carregando || disabled;
  return (
    <button
      onClick={onClick}
      disabled={off}
      title={children ? undefined : "Baixar PDF"}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: pequeno ? "5px 10px" : "7px 14px", borderRadius: 7,
        border: "1px solid #c7d2fe", background: "#eef2ff", color: off ? "#a5b4fc" : "#3730a3",
        fontSize: pequeno ? 11 : 12, fontWeight: 600, cursor: off ? "default" : "pointer", fontFamily: "inherit", ...style,
      }}
    >
      {carregando ? <Loader2 size={12} className="spin" /> : <Download size={12} />} {children}
    </button>
  );
}

const th = { padding: "9px 12px", fontWeight: 700 };
const td = { padding: "10px 12px" };
